/**
 * Audio Manager for recording user input and text-to-speech output
 */

interface SpeechOptions {
  rate?: number;
  pitch?: number;
  volume?: number;
}

interface RecordingState {
  isRecording: boolean;
  hasStream: boolean;
  recordingDuration: number;
}

interface SupportInfo {
  mediaRecorder: boolean;
  getUserMedia: boolean;
  speechSynthesis: boolean;
  webAudio: boolean;
}

export class AudioManager {
  private isRecording: boolean;
  private mediaRecorder: MediaRecorder | null;
  private audioStream: MediaStream | null;
  private audioChunks: Blob[];
  private recordingStartTime: number | null;

  // TTS setup
  private speechSynthesis: SpeechSynthesis | null;

  // Web Audio for sound effects
  private audioContext: AudioContext | null;
  private audioContextDisabled: boolean = false;

  // Event callbacks
  public onRecordingStart: (() => void) | null;
  public onRecordingStop: (() => void) | null;
  public onAudioData: ((audioBlob: Blob, duration: number) => void) | null;
  public onTTSStart: ((text: string) => void) | null;
  public onTTSEnd: (() => void) | null;
  public onError: ((message: string) => void) | null;

  constructor() {
    this.isRecording = false;
    this.mediaRecorder = null;
    this.audioStream = null;
    this.audioChunks = [];
    this.recordingStartTime = null;

    // TTS setup
    this.speechSynthesis = typeof window !== 'undefined' ? window.speechSynthesis : null;

    // Web Audio setup - lazy initialization
    this.audioContext = null;

    // Event callbacks
    this.onRecordingStart = null;
    this.onRecordingStop = null;
    this.onAudioData = null;
    this.onTTSStart = null;
    this.onTTSEnd = null;
    this.onError = null;
  }

  /**
   * Initialize audio system and request permissions
   */
  async initialize() {
    try {
      // Request microphone permission
      this.audioStream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      console.log('Audio system initialized successfully');
      return true;
    } catch (error) {
      console.error('Failed to initialize audio:', error);
      this.handleError('Failed to access microphone. Please check permissions.');
      return false;
    }
  }

  /**
   * Start recording audio
   */
  async startRecording() {
    if (!this.audioStream) {
      const initialized = await this.initialize();
      if (!initialized) return false;
    }

    if (this.isRecording) {
      console.warn('Already recording');
      return false;
    }

    try {
      this.audioChunks = [];
      if (!this.audioStream) {
        throw new Error('Audio stream not available');
      }
      this.mediaRecorder = new MediaRecorder(this.audioStream);
      
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        const audioBlob = new Blob(this.audioChunks, { type: 'audio/wav' });
        this.handleRecordingComplete(audioBlob);
      };

      this.mediaRecorder.onerror = (event) => {
        this.handleError(`Recording error: ${event.error}`);
      };

      this.recordingStartTime = Date.now();
      this.mediaRecorder.start();
      this.isRecording = true;
      
      if (this.onRecordingStart) {
        this.onRecordingStart();
      }
      
      console.log('Recording started');
      return true;
    } catch (error) {
      console.error('Failed to start recording:', error);
      this.handleError('Failed to start recording');
      return false;
    }
  }

  /**
   * Stop recording audio
   */
  stopRecording() {
    if (!this.isRecording || !this.mediaRecorder) {
      console.warn('Not currently recording');
      return;
    }

    try {
      this.mediaRecorder.stop();
      this.isRecording = false;
      
      if (this.onRecordingStop) {
        this.onRecordingStop();
      }
      
      console.log('Recording stopped');
    } catch (error) {
      console.error('Failed to stop recording:', error);
      this.handleError('Failed to stop recording');
    }
  }

  /**
   * Handle completed recording
   */
  private handleRecordingComplete(audioBlob: Blob): void {
    const recordingDuration = this.recordingStartTime ? Date.now() - this.recordingStartTime : 0;
    console.log(`Recording complete: ${recordingDuration}ms, ${audioBlob.size} bytes`);

    if (this.onAudioData) {
      this.onAudioData(audioBlob, recordingDuration);
    }
  }

  /**
    * Speak text using Web Speech API
    * @param text - Text to speak
    * @param options - Speech options
    */
   async speak(text: string, options: SpeechOptions = {}): Promise<void> {
     return new Promise((resolve, reject) => {
       if (!this.speechSynthesis) {
         this.handleError('Text-to-speech not supported');
         reject(new Error('TTS not supported'));
         return;
       }

       // Cancel any ongoing speech
       this.speechSynthesis.cancel();

       const utterance = new SpeechSynthesisUtterance(text);

       // Set voice options
       utterance.rate = options.rate || 0.9;
       utterance.pitch = options.pitch || 1.0;
       utterance.volume = options.volume || 1.0;

       // Try to use a clear, neutral voice
       const voices = this.speechSynthesis.getVoices();
       const preferredVoice = voices.find(voice =>
         voice.lang.startsWith('en') &&
         (voice.name.includes('Google') || voice.name.includes('Microsoft'))
       ) || voices.find(voice => voice.lang.startsWith('en')) || voices[0];

       if (preferredVoice) {
         utterance.voice = preferredVoice;
       }

       utterance.onstart = () => {
         console.log(`Speaking: "${text}"`);
         if (this.onTTSStart) {
           this.onTTSStart(text);
         }
       };

       utterance.onend = () => {
         console.log('Speech completed');
         if (this.onTTSEnd) {
           this.onTTSEnd();
         }
         resolve();
       };

       utterance.onerror = (event) => {
         // Don't treat cancellation/interruption as errors if it was intentional
         if (event.error === 'interrupted' || event.error === 'canceled') {
           console.log('TTS cancelled/interrupted - not treating as error');
           resolve(); // Resolve instead of rejecting for cancellations
           return;
         }

         console.error('Speech error:', event.error);
         this.handleError(`Speech error: ${event.error}`);
         reject(new Error(event.error));
       };

       this.speechSynthesis.speak(utterance);
     });
   }

  /**
   * Stop current speech
   */
  stopSpeaking() {
    if (this.speechSynthesis) {
      this.speechSynthesis.cancel();
    }
  }

  /**
   * Ensure AudioContext is properly initialized
   */
  private async ensureAudioContext(): Promise<boolean> {
    // If audio is disabled due to previous errors, don't try again
    if (this.audioContextDisabled) {
      return false;
    }

    // Lazy initialization
    if (!this.audioContext) {
      try {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) {
          console.warn('Web Audio not supported');
          this.audioContextDisabled = true;
          return false;
        }

        this.audioContext = new AudioContextClass();

        // Add error event listener to disable audio on errors
        if (this.audioContext) {
          this.audioContext.addEventListener('statechange', () => {
            // If context enters an error state, disable audio
            if (this.audioContext && this.audioContext.state === 'closed') {
              this.audioContextDisabled = true;
            }
          });
        }
      } catch (error) {
        console.warn('Failed to create AudioContext, disabling audio');
        this.audioContextDisabled = true;
        return false;
      }
    }

    if (!this.audioContext) {
      this.audioContextDisabled = true;
      return false;
    }

    try {
      // Resume context if suspended
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Check if context is running
      if (this.audioContext.state !== 'running') {
        // Don't log warnings for non-running states to avoid console spam
        return false;
      }

      return true;
    } catch (error) {
      // Disable audio on any error to prevent further issues
      this.audioContextDisabled = true;
      return false;
    }
  }

  /**
   * Play a correct answer sound effect (ascending arpeggio)
   */
  async playCorrectSound(): Promise<void> {
    if (!await this.ensureAudioContext()) {
      return;
    }

    const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
    const duration = 0.15; // 150ms per note
    const delay = 0.05; // 50ms delay between notes

    for (let i = 0; i < notes.length; i++) {
      setTimeout(() => {
        this.playTone(notes[i], duration);
      }, i * (duration + delay) * 1000);
    }
  }

  /**
   * Play an incorrect answer sound effect (descending buzz)
   */
  async playIncorrectSound(): Promise<void> {
    if (!await this.ensureAudioContext()) {
      return;
    }

    const frequency = 220; // A3
    const duration = 0.6; // 600ms
    this.playTone(frequency, duration, 'sawtooth');
  }

  /**
   * Play a tone using Web Audio API
   */
  private playTone(frequency: number, duration: number, waveType: OscillatorType = 'sine'): void {
    if (!this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
      oscillator.type = waveType;

      // Create envelope for smooth attack/decay
      const now = this.audioContext.currentTime;
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01); // Quick attack
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + duration); // Decay

      oscillator.start(now);
      oscillator.stop(now + duration);
    } catch (error) {
      // Silently fail - AudioContext errors are handled at a higher level
      this.audioContextDisabled = true;
    }
  }

  /**
   * Convert audio blob to array buffer (for Whisper processing)
   */
  async audioToArrayBuffer(audioBlob: Blob): Promise<ArrayBuffer> {
    try {
      return await audioBlob.arrayBuffer();
    } catch (error) {
      console.error('Failed to convert audio to array buffer:', error);
      throw error;
    }
  }

  /**
   * Get current recording state
   */
  getRecordingState(): RecordingState {
    return {
      isRecording: this.isRecording,
      hasStream: !!this.audioStream,
      recordingDuration: this.recordingStartTime ? Date.now() - this.recordingStartTime : 0
    };
  }

  /**
   * Handle errors
   */
  private handleError(message: string): void {
    console.error('AudioManager error:', message);
    if (this.onError) {
      this.onError(message);
    }
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    this.stopRecording();
    this.stopSpeaking();
    
    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
      this.audioStream = null;
    }
  }

  /**
   * Check if audio features are supported
   */
  static isSupported(): SupportInfo {
    return {
      mediaRecorder: typeof MediaRecorder !== 'undefined',
      getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
      speechSynthesis: typeof speechSynthesis !== 'undefined',
      webAudio: typeof AudioContext !== 'undefined' || typeof (window as any).webkitAudioContext !== 'undefined'
    };
  }
}

export default AudioManager;